/**
 * Ilova qobig'i: marshrutlash, deep link va Telegram BackButton.
 *
 * TZ §10.1 — DEEP LINK MAQSADI SAQLANISHI SHART. Kanal postidan kelgan
 * odam grant sahifasini darhol ko'radi; onboarding oraliqda turmaydi.
 */

import { useEffect, useState } from "react";
import { api } from "./api";
import { lastLang, setLang, t } from "./i18n";
import { ErrorView, Spinner } from "./components";
import ErrorBoundary from "./ErrorBoundary";
import { HolidayDecor } from "./holiday-decor";
import { applyHolidayTheme } from "./holidays";
import type { Holiday } from "./holidays";
import { IconBookmark, IconHome, IconSearch } from "./icons";
import {
  getLastNavigationType,
  getRouteKey,
  getSavedScrollPos,
  goBack,
  navigate,
  useRoute,
} from "./router";
import Detail from "./screens/Detail";
import Home from "./screens/Home";
import Onboarding from "./screens/Onboarding";
import Saved from "./screens/Saved";
import Search from "./screens/Search";
import { initTelegram, supports, webApp } from "./telegram";
import type { MeResponse } from "./types";

type Boot =
  | { state: "loading" }
  | { state: "error"; message: string }
  | { state: "ready"; me: MeResponse };

export default function App() {
  const route = useRoute();
  const [boot, setBoot] = useState<Boot>({ state: "loading" });
  // Bayram BIR MARTA aniqlanadi va shu seans davomida o'zgarmaydi.
  // Yarim tundan o'tib ketgan ilova ertalabgacha kechagi mavzuda
  // qoladi — bu qayta chizishdan ko'ra kamroq g'alati.
  const [holiday, setHoliday] = useState<Holiday | null>(null);

  useEffect(() => {
    initTelegram();
    // Bayram fonini birinchi kadrdayoq qo'yamiz — keyin qo'yilsa
    // foydalanuvchi fon almashganini ko'rib qoladi.
    setHoliday(applyHolidayTheme(new Date(), lastLang()));

    (async () => {
      try {
        // Deep link maqsadini aniqlash: server /api/start, Telegram SDK yoki URL parametrlari
        const [start, me] = await Promise.all([
          api.start().catch(() => ({
            opportunity_id: null, source: null, link_lang: null,
          })),
          api.me(),
        ]);

        // TIL — birinchi navbatda, boshqa hech narsa chizilishidan
        // OLDIN. Aks holda ilova bir zumga o'zbekcha chizilib, keyin
        // boshqa tilga sakrab o'tardi.
        //
        // Server allaqachon hal qilgan (`/api/me`): saqlangan tanlov ->
        // deep link -> Telegram tili -> `uz`. Klient qaytadan
        // aniqlamaydi, aks holda ikki joyda ikki xil qoida bo'lardi.
        await setLang(me.lang);
        // Bayram tilga bog'liq (MultiLang_TZ §8.8): haqiqiy til endi
        // ma'lum, mavzu qayta hisoblanadi. Til o'zgarmagan bo'lsa natija
        // o'sha-o'sha va fon qimirlamaydi.
        setHoliday(applyHolidayTheme(new Date(), me.lang));

        // URL yoki Telegram SDK dan qo'shimcha tekshiruv (fallback)
        let targetOppId = start.opportunity_id;
        let targetSource = start.source;

        if (!targetOppId) {
          const tgParam = webApp?.initDataUnsafe?.start_param;
          const urlParams = new URLSearchParams(window.location.search);
          const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
          const rawParam =
            tgParam ||
            urlParams.get("startapp") ||
            urlParams.get("tgWebAppStartParam") ||
            urlParams.get("start_param") ||
            hashParams.get("tgWebAppStartParam") ||
            hashParams.get("startapp");

          if (rawParam && rawParam.startsWith("o")) {
            const body = rawParam.slice(1);
            const [oppPart, src] = body.split("_");
            const parsedId = parseInt(oppPart, 10);
            if (!isNaN(parsedId)) {
              targetOppId = parsedId;
              targetSource = src || "channel";
            }
          }
        }

        if (targetOppId) {
          navigate(
            targetSource
              ? { name: "detail", id: targetOppId, source: targetSource }
              : { name: "detail", id: targetOppId },
            { replace: true },
          );
        } else if (!me.onboarded && route.name === "home") {
          navigate({ name: "onboarding" }, { replace: true });
        }

        setBoot({ state: "ready", me });
      } catch (err) {
        setBoot({
          state: "error",
          message: err instanceof Error ? err.message : t("app.startFailed"),
        });
      }
    })();
    // Faqat bir marta — deep link boshlang'ich holatga tegishli
  }, []);

  // Telegram BackButton: bosh ekrandan boshqa joyda ko'rinadi.
  // BackButton 6.1 da qo'shilgan — eski mijozda chaqirilsa SDK har
  // safar konsolga ogohlantirish yozadi va hech narsa qilmaydi.
  // Bunday mijozlarda tizimning o'z "orqaga" ishorasi qoladi.
  useEffect(() => {
    const button = supports("6.1") ? webApp?.BackButton : undefined;
    if (!button) return;
    const onClick = () => goBack();
    button.onClick(onClick);
    if (route.name === "home") button.hide();
    else button.show();
    return () => {
      button.offClick(onClick);
    };
  }, [route.name]);

  // Skroll holatini tiklash: Orqaga qaytishda va tablar orasida joyiga qaytadi
  useEffect(() => {
    const navType = getLastNavigationType();
    const key = getRouteKey(route);

    if (navType === "pop" || route.name === "home" || route.name === "search" || route.name === "saved") {
      const savedY = getSavedScrollPos(key);
      if (savedY !== undefined && savedY > 0) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            window.scrollTo({ top: savedY, left: 0, behavior: "instant" });
          });
        });
        return;
      }
    }
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [route]);

  if (boot.state === "loading") {
    return (
      <>
        <HolidayDecor holiday={holiday} />
        <Spinner label="Yuklanmoqda…" />
      </>
    );
  }
  if (boot.state === "error") {
    return (
      <>
        <HolidayDecor holiday={holiday} />
        <ErrorView
          message={boot.message}
          onRetry={() => window.location.reload()}
        />
      </>
    );
  }

  return (
    <div className="app">
      <HolidayDecor holiday={holiday} />
      <main>
        {/*
          To'siq ekran ichida: pastki panel xato paytida ham qoladi va
          odam boshqa bo'limga o'ta oladi. `resetKey` marshrutga bog'langan
          — boshqa ekranga o'tilganda to'siq o'zini tiklaydi.
        */}
        <ErrorBoundary resetKey={JSON.stringify(route)}>
          {route.name === "onboarding" ? (
            <Onboarding onDone={() => void api.me().then((me) => setBoot({ state: "ready", me }))} />
          ) : route.name === "detail" ? (
            <Detail id={route.id} source={route.source} />
          ) : (
            <>
              <div style={{ display: route.name === "home" ? "block" : "none" }}>
                <Home holiday={holiday} />
              </div>
              <div style={{ display: route.name === "search" ? "block" : "none" }}>
                <Search
                  query={route.name === "search" ? route.query : {}}
                  active={route.name === "search"}
                />
              </div>
              <div style={{ display: route.name === "saved" ? "block" : "none" }}>
                <Saved active={route.name === "saved"} />
              </div>
            </>
          )}
        </ErrorBoundary>
      </main>

      {route.name !== "onboarding" ? (
        <nav className="tabbar">
          <button
            className={`tab${route.name === "home" ? " tab-active" : ""}`}
            type="button"
            aria-current={route.name === "home" ? "page" : undefined}
            onClick={() => navigate({ name: "home" })}
          >
            <IconHome size={21} />
            <span>{t("nav.home")}</span>
          </button>
          <button
            className={`tab${route.name === "search" ? " tab-active" : ""}`}
            type="button"
            aria-current={route.name === "search" ? "page" : undefined}
            onClick={() => navigate({ name: "search", query: {} })}
          >
            <IconSearch size={21} />
            <span>{t("nav.search")}</span>
          </button>
          <button
            className={`tab${route.name === "saved" ? " tab-active" : ""}`}
            type="button"
            aria-current={route.name === "saved" ? "page" : undefined}
            onClick={() => navigate({ name: "saved" })}
          >
            <IconBookmark size={21} />
            <span>{t("nav.saved")}</span>
          </button>
        </nav>
      ) : null}
    </div>
  );
}
